import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm } from "react-hook-form";

import "./Quotation.scss";
import useConfirm from "hooks/useConfirm";
import { modifyDraftData } from "helpers/quotationFormHelper";
import errorToast from "helpers/errorToast";
import { confirmQuotation, editQuotation } from "api";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { ReactComponent as MenuIcon } from "assets/images/icons/Menu.svg";
import { ReactComponent as EditIcon } from "assets/images/icons/SubmitIcon.svg";
import { ReactComponent as CopyIcon } from "assets/images/icons/CopyToClipboard.svg";
import { ReactComponent as CircleCross } from "assets/images/icons/CircleCross.svg";
import { ReactComponent as ApproveIcon } from "assets/images/icons/TopNavIcons/SubmitPOD.svg";
import { ReactComponent as MiniTenderIcon } from "assets/images/icons/MiniTender.svg";
import QuotationDuplicateDialog from "./QuotationDuplicateDialog";
import ExtendQuoteDialog from "./ExtendQuoteDialog";
import CancelDialog from "./CancelDialog";
import EditScheduleDialog from "./EditScheduleDialog";

function QuotationAction(props: any) {
  const { reference, refetchQuotationLists } = props;
  const [show, setShow] = useState(false);
  const [showCanceldialog, setShowCanceldialog] = useState(false);
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [showEditScheduleDialog, setShowEditScheduleDialog] = useState(false);
  const [draftId, setDraftId] = useState("");
  const { id } = useParams();
  const { data: shipmentData }: any = useGetShipmentBasic(id!, {
    enabled: !!id,
    refetchOnWindowFocus: true,
  });

  const { confirm, ConfirmModal } = useConfirm();
  const navigate = useNavigate();
  const { reset } = useForm();
  const ref = useRef<HTMLDivElement>(null);
  const intl = useIntl();
  const { status, created_on_newui, general } = reference;
  const canEditOnDuplicateOnNewUI = reference?.general?.is_rate_card_quote || created_on_newui;

  useEffect(() => {
    const checkIfClickedOutside = (e: any) => {
      if (show && ref?.current && !ref?.current.contains(e.target)) {
        setShow(false);
      }
    };
    document.addEventListener("mousedown", checkIfClickedOutside);
    return () => {
      document.removeEventListener("mousedown", checkIfClickedOutside);
    };
  }, [show]);

  const handleShowMenu = (e: any) => {
    e.stopPropagation();
    setShow(!show);
  };

  const handleEditClick = async (data: any) => {
    if (!created_on_newui && !reference?.general?.is_rate_card_quote) {
      setShow(false);
      await confirm({
        title: intl.formatMessage({
          id: "quote.submitOldUIAlertTitle",
          defaultMessage: "This quote was submitted on the Old UI",
        }),
        message: intl.formatMessage({
          id: "quote.submitOldUIAlertMessage",
          defaultMessage: "Please cancel the quote and re-submit the quote on the Next UI.",
        }),
        disableCancel: true,
      });
      return;
    }

    editQuotation(reference.reference)
      .then((res: any) => {
        const existingData = { ...res.data.data };
        const updatedData = {
          ...existingData,
          schedule: {
            ...existingData.schedule,
            ...data.schedule,
          },
        };
        reset(modifyDraftData(updatedData));
        setDraftId(res.data.reference_number);
        if (general.is_rate_card_quote) {
          setShowEditScheduleDialog(true);
        } else {
          navigate(`${location.pathname}/quotation/add?active_step=service&draft_id=${res.data.reference_number}`);
        }
      })
      .catch((err) => errorToast(err));
  };

  const handleConfirmQuote = () => {
    confirmQuotation(reference.reference)
      .then(() => {
        refetchQuotationLists();
        toast(
          intl.formatMessage({
            id: "quote.confirmSuccessMsg",
            defaultMessage: "Quote confirmed successfully",
          }),
          { type: "success" },
        );
      })
      .catch((error) => errorToast(error));
  };

  return (
    <>
      <ConfirmModal />
      <div className="provider-quotation">
        <button
          type="button"
          className="menu-btn"
          onClick={handleShowMenu}
          disabled={["canceled", "canceled_by_provider", "lost", "booked", "booked_as_duplicate"].includes(status)}
        >
          <MenuIcon />
        </button>
        <CancelDialog
          isOpen={showCanceldialog}
          close={() => {
            setShowCanceldialog(false);
          }}
          referenceId={reference.reference}
          refetchQuotationLists={refetchQuotationLists}
        />
        <ExtendQuoteDialog
          isOpen={showExtendDialog}
          close={() => {
            setShowExtendDialog(false);
          }}
          reference={reference}
          refetchQuotationLists={refetchQuotationLists}
        />
        <QuotationDuplicateDialog
          isOpen={showDuplicateDialog}
          close={() => {
            setShowDuplicateDialog(false);
          }}
          referenceId={reference.reference}
        />
        <EditScheduleDialog
          quotation={reference.reference}
          isOpen={showEditScheduleDialog}
          close={() => {
            setShowEditScheduleDialog(false);
          }}
          refetchQuotationLists={refetchQuotationLists}
          draftId={draftId}
        />
        {show && (
          <div
            ref={ref}
            className="d-flex flex-column justify-content-center border-grey quote-action-tooltip"
            style={{ width: "191px", height: "auto", padding: "16px" }}
          >
            {["new", "confirmation_needed"].includes(status) && (
              <div
                className="d-flex align-items-center cursor-pointer font-b3 bg-hover-palegrey br-16"
                id="confirm-quote"
                role="presentation"
                onClick={(e) => {
                  e.stopPropagation();
                  handleConfirmQuote();
                }}
                style={{ height: "40px", paddingLeft: "7px" }}
              >
                <ApproveIcon width="24" height="24" />
                <span className="font-b2 ml-2 p-4">
                  <FormattedMessage id="quote.quotation.confirmQuote" defaultMessage="Confirm quote" />
                </span>
              </div>
            )}
            {["booked", "submitted", "open_to_book", "confirmation_needed"].includes(status) &&
              shipmentData?.organization?.quoting_deadline_seconds_remaining > 0 && (
                <div
                  className="d-flex align-items-center cursor-pointer font-b3 bg-hover-palegrey br-16"
                  id="edit-quote"
                  role="presentation"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(reference);
                  }}
                  style={{ height: "40px", paddingLeft: "7px" }}
                >
                  <EditIcon height="24" width="24" />
                  <span className="font-b2 ml-2 p-4">
                    {general.is_rate_card_quote ? (
                      <FormattedMessage id="quote.quotation.editSchedule" defaultMessage="Edit schedule" />
                    ) : (
                      <FormattedMessage id="quote.quotation.editQuote" defaultMessage="Edit quote" />
                    )}
                  </span>
                </div>
              )}
            {!["booked_as_duplicate"].includes(status) && (
              <div
                className="d-flex align-items-center cursor-pointer font-b3 bg-hover-palegrey br-16"
                id="extend-quote"
                role="presentation"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowExtendDialog(true);
                }}
                style={{ height: "40px", paddingLeft: "7px" }}
              >
                <MiniTenderIcon height="24" width="24" />
                <span className="font-b2 ml-2 p-4">
                  <FormattedMessage id="quote.quotation.extendQuote" defaultMessage="Extend quote" />
                </span>
              </div>
            )}
            {["booked_as_duplicate", "booked", "submitted", "open_to_book", "confirmation_needed"].includes(status) &&
              shipmentData?.organization?.quoting_deadline_seconds_remaining > 0 &&
              canEditOnDuplicateOnNewUI && (
                <div
                  className="d-flex align-items-center cursor-pointer font-b3 bg-hover-palegrey br-16"
                  id="duplicate-quote"
                  role="presentation"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDuplicateDialog(true);
                  }}
                  style={{ height: "40px", paddingLeft: "7px" }}
                >
                  <CopyIcon height="24" width="24" />
                  <span className="font-b2 ml-2 text-wrap p-4">
                    <FormattedMessage id="new-request.packing-list.duplicate" defaultMessage="Duplicate" />
                  </span>
                </div>
              )}
            {!["booked_as_duplicate"].includes(status) && (
              <div
                className="d-flex align-items-center cursor-pointer font-b3 bg-hover-palegrey br-16"
                id="cancel-quote"
                role="presentation"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCanceldialog(true);
                }}
                style={{ height: "40px", paddingLeft: "7px" }}
              >
                <CircleCross stroke="black" height="24" width="24" />
                <span className="font-b2 ml-2 p-4">
                  <FormattedMessage id="general.cancelQuote" defaultMessage="Cancel quote" />
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default QuotationAction;
